import { Star, Download, FileText, Sparkles, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { PlusBadge } from "@/components/PlusBadge";
import { useAuth } from "@/contexts/AuthContext";
import type { NoteWithSeller } from "@/hooks/useNotes";
import { transformStorageImage } from "@/lib/imageTransform";

function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function NoteCard({ note, isPromoted }: { note: NoteWithSeller; isPromoted?: boolean }) {
  const { subscription } = useAuth();
  const price = Number(note.price);
  const discountedPrice = subscription.isPlus ? price * 0.85 : null;

  const hasPreview = note.preview_images && note.preview_images.length > 0;
  const isRealUser = note.seller_is_seed === false;
  const meetsThreshold = (note.review_count || 0) >= 15 && Number(note.rating || 0) >= 4.5;
  const isTopRated = isRealUser || meetsThreshold;

  return (
    <Link to={`/note/${note.id}`} className="glass-card-hover block overflow-hidden">
      <div className="relative h-40 flex items-center justify-center overflow-hidden bg-muted/30">
        {hasPreview ? (
          <img
            src={transformStorageImage(note.preview_images![0], { width: 600, height: 400, quality: 60 })}
            alt={`Preview of ${note.title}`}
            width={600}
            height={400}
            className="w-full h-full object-cover select-none pointer-events-none"
            loading="lazy"
            decoding="async"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />
        ) : (
          <div className="gradient-primary w-full h-full flex items-center justify-center">
            <FileText className="h-12 w-12 text-primary-foreground/50" />
          </div>
        )}
        <Badge className="absolute top-3 right-3 bg-background/90 text-foreground border-0 text-xs">
          {note.education_level}
        </Badge>
        {isPromoted && (
          <Badge className="absolute top-3 left-3 bg-accent text-accent-foreground border-0 text-[10px] gap-1">
            <Sparkles className="h-3 w-3" /> Sponsored
          </Badge>
        )}
        {isTopRated && (
          <Badge
            className={`absolute ${isPromoted ? "top-10" : "top-3"} left-3 bg-primary text-primary-foreground border-0 text-[10px] gap-1 shadow-sm`}
          >
            <Award className="h-3 w-3" /> Top Rated
          </Badge>
        )}
      </div>

      <div className="p-4">
        <Link
          to={`/course/${encodeURIComponent(note.education_level)}/${encodeURIComponent(note.course_code)}`}
          onClick={(e) => e.stopPropagation()}
          className="text-xs font-medium text-primary hover:underline mb-1 inline-block"
        >
          {note.subject} / {note.education_level}
        </Link>
        <h3 className="font-display font-semibold text-foreground line-clamp-2 mb-2 leading-snug">
          {note.title}
        </h3>
        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{note.description}</p>

        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
          <span className="flex items-center gap-1">
            <Star className="h-3.5 w-3.5 fill-accent text-accent" />
            {Number(note.rating || 0).toFixed(1)}
          </span>
          <span className="flex items-center gap-1">
            <Download className="h-3.5 w-3.5" />
            {note.download_count || 0}
          </span>
          {note.pages ? <span>{note.pages} pages</span> : null}
        </div>

        <div className="flex items-center justify-between border-t border-border pt-3">
          <div className="flex items-center gap-2">
            <Link
              to={`/user/${note.seller_id}`}
              onClick={(e) => e.stopPropagation()}
              className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary hover:ring-2 hover:ring-primary/30 transition-all overflow-hidden"
            >
              {note.profiles?.avatar_url ? (
                <img
                  src={transformStorageImage(note.profiles.avatar_url, { width: 48, height: 48, quality: 75 })}
                  alt={`${note.profiles.display_name || "Seller"} profile picture`}
                  width={48}
                  height={48}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              ) : (
                getInitials(note.profiles?.display_name)
              )}
            </Link>
            <Link
              to={`/user/${note.seller_id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              {note.profiles?.display_name || "Anonymous"}
            </Link>
            {note.profiles?.is_plus && <PlusBadge />}
          </div>
          <div className="text-right">
            {discountedPrice !== null ? (
              <div className="flex flex-col items-end">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground line-through">€{price.toFixed(2)}</span>
                  <span className="font-display font-bold text-primary">€{discountedPrice.toFixed(2)}</span>
                </div>
                <span className="text-[10px] text-muted-foreground/70">All sales are final. No refunds.</span>
              </div>
            ) : (
              <div className="flex flex-col items-end">
                <span className="font-display font-bold text-foreground">€{price.toFixed(2)}</span>
                <span className="text-[10px] text-muted-foreground/70">All sales are final. No refunds.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
